import ContactAvatar from '$tablet-app/src/app/components/Contact/ContactAvatar';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { Button } from '@akin-home/buttons/native';
import { ContactModel } from '@akin-home/contacts';
import { useListContactsForHabitat } from '@akin-home/contacts/react';
import { useHabitatID } from '@akin-home/habitats/react';
import { Text } from '@akin-home/text/native';
import { colors } from '@akin-home/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React from 'react';
import { FlatList, StyleSheet, View, ViewProps } from 'react-native';
import styled from 'styled-components/native';
import Spacer from '../../components/Spacer';
import { useMainDrawerNavigaton } from '../../navigation/hooks';
import DashboardPanel from '../DashboardPanel';

interface Props extends ViewProps {
  small?: boolean;
}

const ContactsPanel = ({ small, ...props }: Props) => {
  const habitatID = useHabitatID();
  const { navigate } = useMainDrawerNavigaton();

  const { data: contacts, isLoading } = useListContactsForHabitat(habitatID);

  const onPress = (contact: ContactModel) =>
    navigate('Contacts', { contact_id: contact.id });

  const onPressSearch = () => navigate('Contacts');

  if (!isLoading && !contacts.length) {
    return (
      <EmptyPanel>
        <EmptyTitleRow>
          <FontAwesomeIcon
            icon="user-friends"
            size={25}
            color={colors.pink.primary}
          />
          <Text variant="h3" weight="semibold">
            Contacts
          </Text>
        </EmptyTitleRow>

        <View>
          <Button
            variant="secondary"
            icon="plus"
            size="large"
            onPress={onPressSearch}
          >
            Add your first contact
          </Button>
        </View>
      </EmptyPanel>
    );
  }

  return (
    <Panel {...props}>
      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <>
          <Button
            icon="search"
            variant="secondary"
            onPress={onPressSearch}
            size={!small ? 'large' : undefined}
          >
            Contacts
          </Button>

          {!small ? (
            <>
              <Spacer flex={1} />
              <FlatList
                data={contacts.slice(0, 10)}
                contentContainerStyle={styles.horizontal_flatlist}
                horizontal
                renderItem={({ item }) => (
                  <VertialContactItem contact={item} onPress={onPress} />
                )}
              />
            </>
          ) : (
            <FlatList
              data={contacts.slice(0, 10)}
              renderItem={({ item }) => (
                <ContactItem contact={item} onPress={onPress} />
              )}
            />
          )}
        </>
      )}
    </Panel>
  );
};

interface ContactItemProps {
  contact: ContactModel;
  onPress: (contact: ContactModel) => void;
}

const ContactItem = (props: ContactItemProps) => {
  const { contact } = props;

  const onPress = () => props.onPress(contact);

  return (
    <Item onPress={onPress}>
      <Avatar contact={contact} />
      <Title variant="h3" numberOfLines={1}>
        {contact.name}
      </Title>
    </Item>
  );
};

const VertialContactItem = (props: ContactItemProps) => {
  const { contact } = props;

  const onPress = () => props.onPress(contact);

  return (
    <VerticalItem onPress={onPress}>
      <LargeAvatar contact={contact} />
      <Title weight="semibold" numberOfLines={1}>
        {contact.name}
      </Title>
    </VerticalItem>
  );
};

const Item = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
`;

const VerticalItem = styled.TouchableOpacity`
  align-items: center;
  gap: 16px;
`;

const Title = styled(Text)`
  flex: 1;
`;

const Avatar = styled(ContactAvatar)`
  height: 64px;
  width: 64px;
  border-radius: 25px;
`;

const LargeAvatar = styled(ContactAvatar)`
  height: 100px;
  width: 100px;
  border-radius: 30px;
`;

const Panel = styled(DashboardPanel)`
  flex: 1;
  padding: 16px;
`;

const EmptyPanel = styled(DashboardPanel)`
  flex: 1;
  background-color: ${colors.pink.background};
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const EmptyTitleRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 14px;
`;

const styles = StyleSheet.create({
  horizontal_flatlist: {
    gap: 16,
  },
});

export default ContactsPanel;
